import { useEffect } from "react";

/**
 * <Seo> · componente ligero que actualiza title + meta description +
 * canonical en función de la ruta actual. Pensado para rutas públicas
 * del SPA (Landing, Legal, páginas compartidas) que necesitan título
 * único para no romper SEO.
 *
 * No introduce dependencias (sin react-helmet). Usa el DOM directamente
 * en un useEffect — Googlebot renderiza JS y lee el DOM final, así que
 * los cambios aquí sí quedan indexados.
 *
 * Uso típico:
 *   <Seo
 *     title="Diagnóstico de Gestión del Talento · OptimizaRRHH"
 *     description="Audita tu talento en 7 minutos..."
 *     canonical="https://talenthealthcheck.com/"
 *   />
 */
export default function Seo({ title, description, canonical, image, type = "website" }) {
  useEffect(() => {
    if (title) {
      document.title = title;
    }
    const upsert = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(attrs.tag);
        Object.entries(attrs.initial || {}).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      Object.entries(attrs.set).forEach(([k, v]) => el.setAttribute(k, v));
    };
    if (description) {
      upsert('meta[name="description"]', {
        tag: "meta",
        initial: { name: "description" },
        set: { name: "description", content: description },
      });
      upsert('meta[property="og:description"]', {
        tag: "meta",
        initial: { property: "og:description" },
        set: { property: "og:description", content: description },
      });
      upsert('meta[name="twitter:description"]', {
        tag: "meta",
        initial: { name: "twitter:description" },
        set: { name: "twitter:description", content: description },
      });
    }
    if (title) {
      upsert('meta[property="og:title"]', {
        tag: "meta",
        initial: { property: "og:title" },
        set: { property: "og:title", content: title },
      });
      upsert('meta[name="twitter:title"]', {
        tag: "meta",
        initial: { name: "twitter:title" },
        set: { name: "twitter:title", content: title },
      });
    }
    if (canonical) {
      upsert('link[rel="canonical"]', {
        tag: "link",
        initial: { rel: "canonical" },
        set: { rel: "canonical", href: canonical },
      });
      upsert('meta[property="og:url"]', {
        tag: "meta",
        initial: { property: "og:url" },
        set: { property: "og:url", content: canonical },
      });
    }
    if (image) {
      upsert('meta[property="og:image"]', {
        tag: "meta",
        initial: { property: "og:image" },
        set: { property: "og:image", content: image },
      });
      upsert('meta[name="twitter:image"]', {
        tag: "meta",
        initial: { name: "twitter:image" },
        set: { name: "twitter:image", content: image },
      });
    }
    if (type) {
      upsert('meta[property="og:type"]', {
        tag: "meta",
        initial: { property: "og:type" },
        set: { property: "og:type", content: type },
      });
    }
  }, [title, description, canonical, image, type]);

  return null;
}
